import { supabase } from '../supabase.js';
import { buildLearnerPayslipsPdf, rowFromStoredPayslip } from './payslip-pdf-core.mjs';

/* ============================================================
   Learner payslips in the browser (docs/learner-payslips.md).
   FINAL and PAID payslips are downloaded exactly as stored at
   finalise. A DRAFT can only be previewed, from its stored row,
   stamped DRAFT. Nothing here works out a stipend.
============================================================ */

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function payslipFileName(lp) {
  const who = lp.national_id || lp.student_uuid;
  return `payslip-${who}-${lp.period_year}-${String(lp.period_month).padStart(2, '0')}.pdf`;
}

/** Download the PDF stored at finalise. */
export async function downloadStoredPayslip(lp) {
  const { data, error } = await supabase.storage.from('payslips').download(lp.pdf_path);
  if (error) throw new Error(`Stored payslip could not be downloaded: ${error.message}`);
  saveBlob(data, payslipFileName(lp));
}

async function logo() {
  try {
    const resp = await fetch('/londmarx%20logo.png');
    if (resp.ok) return await resp.arrayBuffer();
  } catch { /* no logo — text fallback */ }
  return null;
}

/** Preview a draft payslip from its stored row. */
export async function previewDraftPayslip(lp, org) {
  const bytes = await buildLearnerPayslipsPdf(
    [{ row: rowFromStoredPayslip(lp), year: lp.period_year, month: lp.period_month, draft: true }], org, await logo());
  saveBlob(new Blob([bytes], { type: 'application/pdf' }), payslipFileName(lp).replace('.pdf', '-DRAFT.pdf'));
}

/**
 * The PDFs of some stored payslips, in the order given: final/paid exactly as stored at
 * finalise, a draft drawn from its row and stamped DRAFT (for Payslips & documents downloads).
 * @returns {Promise<Uint8Array[]>}
 */
export async function payslipPdfs(lps, org) {
  let logoBytes;
  const out = [];
  for (const lp of lps) {
    if (lp.status !== 'draft' && lp.pdf_path) {
      const { data, error } = await supabase.storage.from('payslips').download(lp.pdf_path);
      if (error) throw new Error(`Stored payslip could not be downloaded: ${error.message}`);
      out.push(new Uint8Array(await data.arrayBuffer()));
    } else {
      logoBytes ??= await logo();
      out.push(await buildLearnerPayslipsPdf(
        [{ row: rowFromStoredPayslip(lp), year: lp.period_year, month: lp.period_month, draft: lp.status === 'draft' }], org, logoBytes));
    }
  }
  return out;
}
